import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowUpRight,
  CheckCircle2,
  AlertTriangle,
  Search,
  Cog,
  Sparkles,
} from "lucide-react";
import { PageHeader, SectionHeader } from "../components/Section";
import { CONSULTING_SERVICES } from "../data/site";

const ICONS = {
  "business-turnaround": AlertTriangle,
  "business-review": Search,
  "operational-excellence": Cog,
  "ai-agency": Sparkles,
};

const TURNAROUND_PHASES = [
  { phase: "Day 1–7", title: "Mobilise", body: "Diagnostic team on site. Cash, banks, customers, suppliers, employees — stabilised in week one." },
  { phase: "Day 8–30", title: "Stabilise", body: "13-week cash flow, working-capital interventions, critical-vendor management. Stop the bleed." },
  { phase: "Day 31–90", title: "Restructure", body: "Footprint, SKU, headcount and cost-base interventions. Sequenced, quantified, executed." },
  { phase: "Day 91–180", title: "Reset", body: "New operating rhythm, KPI architecture, leadership cadence — a business that does not relapse." },
];

const Consulting = () => {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [hash]);

  return (
    <div data-testid="page-consulting">
      <PageHeader
        overline="Consulting"
        title={<>Strategic operational <span className="text-[#E63946]">recovery & growth.</span></>}
        kicker="When the brief is bigger than a course, our consulting team works alongside leadership — diagnosing, restructuring and rebuilding operations to deliver outcomes that show up in the management accounts."
      />

      {CONSULTING_SERVICES.map((s, idx) => {
        const Icon = ICONS[s.id];
        const reverse = idx % 2 === 1;
        return (
          <section
            key={s.id}
            id={s.id}
            data-testid={`consulting-${s.id}`}
            className={`border-b border-black/10 ${idx % 2 === 0 ? "bg-[#F7F7F8]" : "bg-white"}`}
          >
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
              <div className={`grid lg:grid-cols-12 gap-12 ${reverse ? "lg:[direction:rtl]" : ""}`}>
                <div className="lg:col-span-4 lg:[direction:ltr]">
                  <div className="flex items-center gap-3 mb-8">
                    <Icon className="w-8 h-8 text-[#E63946]" strokeWidth={1.5} />
                    <span className="font-mono-tech text-[11px] tracking-widest text-zinc-500">
                      0{idx + 1} / 04
                    </span>
                  </div>
                  <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tighter leading-[1.02] text-black">
                    {s.title}
                  </h2>
                  <p className="mt-5 text-base lg:text-lg italic text-zinc-500">{s.tagline}</p>
                  <p className="mt-8 text-zinc-700 leading-relaxed">{s.summary}</p>

                  <p className="mt-10 text-xs tracking-[0.22em] uppercase text-[#E63946] font-semibold">
                    Outcomes
                  </p>
                  <ul className="mt-4 space-y-3">
                    {s.outcomes.map((o) => (
                      <li key={o} className="flex items-start gap-2.5 text-sm text-black">
                        <CheckCircle2 className="w-4 h-4 text-[#E63946] flex-shrink-0 mt-0.5" strokeWidth={1.75} />
                        <span className="leading-relaxed">{o}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/contact"
                    data-testid={`consulting-cta-${s.id}`}
                    className="group mt-10 inline-flex items-center gap-2 bg-black text-white px-6 py-3 text-sm font-semibold hover:bg-[#E63946] transition-colors"
                  >
                    Discuss this engagement <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>

                <div className="lg:col-span-8 lg:[direction:ltr]">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10 border border-black/10">
                    {s.pillars.map((p, i) => (
                      <div
                        key={p.title}
                        data-testid={`pillar-${s.id}-${i}`}
                        className="bg-white p-8 lg:p-10"
                      >
                        <p className="font-mono-tech text-[11px] tracking-widest text-zinc-500">
                          0{i + 1}
                        </p>
                        <h3 className="mt-4 font-display text-xl lg:text-2xl font-bold tracking-tight text-black">
                          {p.title}
                        </h3>
                        <p className="mt-3 text-sm leading-relaxed text-zinc-600">{p.body}</p>
                      </div>
                    ))}
                  </div>

                  {s.id === "business-turnaround" && (
                    <div className="mt-10 border border-black/10 bg-white">
                      <div className="p-8 lg:p-10 border-b border-black/10">
                        <p className="text-xs tracking-[0.22em] uppercase text-[#E63946] font-semibold">
                          The 180-day turnaround
                        </p>
                        <h3 className="mt-3 font-display text-2xl lg:text-3xl font-bold tracking-tighter">
                          A sequenced response, not a checklist.
                        </h3>
                      </div>
                      <ol className="divide-y divide-black/10">
                        {TURNAROUND_PHASES.map((t, i) => (
                          <li
                            key={t.phase}
                            data-testid={`turnaround-phase-${i}`}
                            className="grid grid-cols-12 gap-4 p-6 lg:p-8"
                          >
                            <div className="col-span-3 lg:col-span-2 font-mono-tech text-[11px] tracking-widest text-zinc-500">
                              {t.phase}
                            </div>
                            <div className="col-span-9 lg:col-span-3">
                              <h4 className="font-display text-lg font-bold text-black">
                                {t.title}
                              </h4>
                            </div>
                            <div className="col-span-12 lg:col-span-7 text-sm text-zinc-600 leading-relaxed">
                              {t.body}
                            </div>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        );
      })}

      <section className="bg-[#09090B] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          <SectionHeader
            overline="How we engage"
            title={<><span className="text-white">Five steps from</span> <span className="text-[#E63946]">first call to first impact.</span></>}
          />
          <ol className="grid grid-cols-1 md:grid-cols-5 gap-px bg-zinc-800 border border-zinc-800">
            {["Discovery call", "Scoping diagnostic", "Engagement letter", "Mobilisation", "Tracked delivery"].map(
              (s, i) => (
                <li
                  key={s}
                  data-testid={`engage-step-${i}`}
                  className="bg-[#09090B] p-8 lg:p-10"
                >
                  <p className="font-mono-tech text-[11px] tracking-widest text-zinc-500">
                    0{i + 1} / 05
                  </p>
                  <h3 className="mt-6 font-display text-xl font-bold text-white">{s}</h3>
                </li>
              )
            )}
          </ol>

          <div className="mt-16 flex flex-wrap gap-3">
            <Link
              to="/contact"
              data-testid="consulting-bottom-cta"
              className="group inline-flex items-center gap-2 bg-[#E63946] text-white px-7 py-4 text-sm font-semibold hover:bg-[#D32F2F] transition-colors"
            >
              Start the conversation <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/clients"
              data-testid="consulting-clients-cta"
              className="group inline-flex items-center gap-2 border border-white text-white px-7 py-4 text-sm font-semibold hover:bg-white hover:text-black transition-colors"
            >
              See where we've worked
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Consulting;
